import React from 'react';
import { Box, Avatar, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { 
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Person as PersonIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';

/**
 * Section Avatar et Actions - Avatar propriétaire et bouton favoris
 */
const AvatarActionsSection = ({ 
  listing, 
  layout = 'comfortable',
  isFavorite = false,
  onFavoriteToggle,
  t
}) => {
  const theme = useTheme();
  const ownerData = listing.owner || listing.seller || null;
  
  if (layout === 'compact') {
    return (
      <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', p: 0.5, gap: 0.5, alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Avatar du propriétaire */}
        <Tooltip title={ownerData?.name || t('listing.owner.anonymous')}>
          <Avatar
            src={ownerData?.profilePicture || ownerData?.avatar}
            alt={ownerData?.name || t('listing.owner.anonymous')}
            sx={{ 
              width: 42,
              height: 42,
              fontSize: '1rem',
              backgroundColor: ownerData?.profilePicture || ownerData?.avatar ? 'transparent' : theme.palette.primary.main,
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: 2
            }}
          >
            {!ownerData?.profilePicture && !ownerData?.avatar && (
              <PersonIcon sx={{ fontSize: '1.2rem', color: 'white' }} />
            )}
          </Avatar>
        </Tooltip>

        <Typography
          variant="caption"
          sx={{
            maxWidth: '100%',
            textAlign: 'center',
            fontWeight: 600,
            lineHeight: 1.1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {ownerData?.name || t('listing.owner.anonymous')}
        </Typography>
        
        {/* Action favoris */}
        <IconButton 
          size="small" 
          onClick={onFavoriteToggle}
          sx={{
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.15)',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              '& .favorite-icon': {
                color: 'error.main',
                transform: 'scale(1.1)',
              }
            }
          }}
        >
          {isFavorite ? 
            <FavoriteIcon 
              className="favorite-icon"
              sx={{ 
                color: 'error.main', 
                fontSize: 24,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }} 
            /> : 
            <FavoriteBorderIcon 
              className="favorite-icon"
              sx={{ 
                fontSize: 24,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }} 
            />
          }
        </IconButton>
      </Box>
    );
  }

  // Pour les autres layouts, l'avatar est dans les CardActions
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar
        src={ownerData?.profilePicture || ownerData?.avatar}
        alt={ownerData?.name || t('listing.owner.anonymous')}
        sx={{ 
          width: 88, 
          height: 88,
          fontSize: '2.2rem',
          backgroundColor: ownerData?.profilePicture || ownerData?.avatar ? 'transparent' : theme.palette.primary.main,
          border: `3px solid ${theme.palette.background.paper}`,
          boxShadow: 4
        }}
      >
        {!ownerData?.profilePicture && !ownerData?.avatar && (
          <PersonIcon sx={{ fontSize: '3.5rem', color: 'white' }} />
        )}
      </Avatar>
      <Box>
        <Typography variant="caption" color="text.primary" fontWeight={500}>
          {ownerData?.name || t('listing.owner.anonymous')}
        </Typography>
        {ownerData?.isVerified && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <VerifiedIcon sx={{ fontSize: 12, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" fontSize="0.65rem">
              {t('listing.owner.verified')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AvatarActionsSection;
